import React, { useState } from 'react';
import { Dialog, Button, TextField, SelectField } from '@scalingworks/react-admin-ui';
import { useNotification } from '@refinedev/core';
import { FiPlus, FiMinus } from 'react-icons/fi';
import { useAddStampsManuallyMutation } from '~/generated/hooks';
import { useInternalTranslate } from '@scalingworks/refine-react-admin/src/hooks/use-internal-translate';

interface AddStampsModalProps {
  open: boolean;
  onClose: () => void;
  userId: string;
  stampCardId?: string;
  templateId?: string;
  currentStamps: number;
  totalStampsRequired: number;
  customerName: string;
  onSuccess?: () => void;
}

export const AddStampsModal: React.FC<AddStampsModalProps> = ({
  open,
  onClose,
  userId,
  stampCardId,
  templateId,
  currentStamps,
  totalStampsRequired,
  customerName,
  onSuccess,
}) => {
  const t = useInternalTranslate();
  const { open: showNotification } = useNotification();

  const [stampsToAdd, setStampsToAdd] = useState(1);
  const [selectedReason, setSelectedReason] = useState('customer_service');
  const [customReason, setCustomReason] = useState('');
  const [notes, setNotes] = useState('');

  // Generate reason options with translations
  const REASON_OPTIONS = [
    {
      value: 'customer_service',
      label: t('stampCard.addStamps.reasonOptions.customer_service', {
        fallback: 'Customer Service Compensation',
      }),
    },
    {
      value: 'promotional',
      label: t('stampCard.addStamps.reasonOptions.promotional', {
        fallback: 'Promotional Campaign',
      }),
    },
    {
      value: 'system_correction',
      label: t('stampCard.addStamps.reasonOptions.system_correction', {
        fallback: 'System Correction',
      }),
    },
    {
      value: 'special_event',
      label: t('stampCard.addStamps.reasonOptions.special_event', {
        fallback: 'Special Event',
      }),
    },
    {
      value: 'other',
      label: t('stampCard.addStamps.reasonOptions.other', {
        fallback: 'Other (specify)',
      }),
    },
  ];

  const { mutate: addStamps, isLoading } = useAddStampsManuallyMutation({
    onSuccess: () => {
      showNotification?.({
        type: 'success',
        message:
          stampsToAdd > 1
            ? t('stampCard.addStamps.success_plural', {
                fallback: `Successfully added ${stampsToAdd} stamps`,
                data: { count: stampsToAdd },
              })
            : t('stampCard.addStamps.success', {
                fallback: 'Successfully added 1 stamp',
                data: { count: 1 },
              }),
      });
      onSuccess?.();
      handleClose();
    },
    onError: (error: any) => {
      showNotification?.({
        type: 'error',
        message: t('stampCard.addStamps.error', {
          fallback: 'Failed to add stamps. Please try again.',
        }),
        description: error.message,
      });
    },
  });

  const handleClose = () => {
    setStampsToAdd(1);
    setSelectedReason('customer_service');
    setCustomReason('');
    setNotes('');
    onClose();
  };

  const handleSubmit = () => {
    const reason = selectedReason === 'other' ? customReason : selectedReason;
    if (!reason.trim()) {
      showNotification?.({
        type: 'error',
        message: t('stampCard.addStamps.reasonRequired', {
          fallback: 'Please provide a reason for adding stamps',
        }),
      });
      return;
    }

    addStamps({
      input: {
        cardId: stampCardId || '',
        reason: reason.trim(),
        stampCount: stampsToAdd,
        internalNotes: notes,
        reasonCategory: selectedReason,
        metadata: {
          notes,
          addedBy: 'admin',
          timestamp: new Date().toISOString(),
        },
      },
    });
  };

  const incrementStamps = () => {
    if (currentStamps + stampsToAdd < totalStampsRequired) {
      setStampsToAdd((prev) => prev + 1);
    }
  };

  const decrementStamps = () => {
    if (stampsToAdd > 1) {
      setStampsToAdd((prev) => prev - 1);
    }
  };

  const newTotalStamps = currentStamps + stampsToAdd;
  const willComplete = newTotalStamps >= totalStampsRequired;

  return (
    <Dialog.Root open={open} onOpenChange={handleClose}>
      <Dialog.Portal>
        <Dialog.Overlay />
        <Dialog.Content className="fixed top-1/3 w-[32rem] max-w-[90vw] overflow-x-hidden">
          <Dialog.Title>
            {t('stampCard.addStamps.title', {
              fallback: 'Add Stamps Manually',
            })}
          </Dialog.Title>

          <div className="space-y-4 py-4">
            {/* Customer Info */}
            <div className="bg-gray-50 p-3 rounded-lg">
              <p className="text-sm text-gray-600">
                {t('stampCard.addStamps.customerLabel', {
                  fallback: 'Customer:',
                })}{' '}
                <span className="font-semibold text-gray-800">{customerName}</span>
              </p>
              <p className="text-sm text-gray-600 mt-1">
                {t('stampCard.addStamps.currentProgress', {
                  fallback: `Current Progress: ${currentStamps} / ${totalStampsRequired}`,
                  data: { current: currentStamps, total: totalStampsRequired },
                })}
              </p>
            </div>

            {/* Stamps to Add */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('stampCard.addStamps.stampsToAddLabel', {
                  fallback: 'Number of Stamps to Add',
                })}
              </label>
              <div className="flex items-center gap-3">
                <Button
                  variant="default"
                  size="sm"
                  onClick={decrementStamps}
                  disabled={stampsToAdd <= 1}
                >
                  <FiMinus />
                </Button>

                <div className="flex-1 text-center">
                  <span className="text-2xl font-bold text-purple-600">{stampsToAdd}</span>
                </div>

                <Button
                  variant="default"
                  size="sm"
                  onClick={incrementStamps}
                  disabled={newTotalStamps >= totalStampsRequired}
                >
                  <FiPlus />
                </Button>
              </div>

              {/* Preview */}
              <div className="mt-3 p-3 bg-purple-50 rounded-lg">
                <p className="text-sm text-purple-700">
                  {t('stampCard.addStamps.newTotal', {
                    fallback: `New Total: ${newTotalStamps} / ${totalStampsRequired}`,
                    data: { new: newTotalStamps, total: totalStampsRequired },
                  })}
                </p>
                {willComplete && (
                  <p className="text-sm text-green-600 font-semibold mt-1">
                    {t('stampCard.addStamps.willComplete', {
                      fallback: '✓ Card will be completed!',
                    })}
                  </p>
                )}
              </div>
            </div>

            {/* Reason */}
            <SelectField
              label={t('stampCard.addStamps.reasonLabel', {
                fallback: 'Reason',
              })}
              value={selectedReason}
              onValue={(value) => setSelectedReason(value)}
              options={REASON_OPTIONS}
              required
            />

            {/* Custom Reason */}
            {selectedReason === 'other' && (
              <TextField
                label={t('stampCard.addStamps.customReasonLabel', {
                  fallback: 'Specify Reason',
                })}
                value={customReason}
                onValue={(value) => setCustomReason(value)}
                placeholder={t('stampCard.addStamps.customReasonPlaceholder', {
                  fallback: 'Enter specific reason...',
                })}
                required
              />
            )}

            {/* Additional Notes */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('stampCard.addStamps.notesLabel', {
                  fallback: 'Additional Notes (Optional)',
                })}
              </label>
              <textarea
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                rows={3}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder={t('stampCard.addStamps.notesPlaceholder', {
                  fallback: 'Any additional information...',
                })}
              />
            </div>
          </div>

          {/* Actions - moved inside Dialog.Content */}
          <div className="flex justify-end gap-2 pt-4 border-t border-gray-200">
            <Button variant="default" onClick={handleClose} disabled={isLoading}>
              {t('cancel', {
                fallback: 'Cancel',
              })}
            </Button>
            <Button variant="solid" onClick={handleSubmit} disabled={isLoading}>
              {isLoading
                ? t('common.loading', { fallback: 'Loading...' })
                : stampsToAdd > 1
                ? t('stampCard.addStamps.submitButton_plural', {
                    fallback: `Add ${stampsToAdd} Stamps`,
                    data: { count: stampsToAdd },
                  })
                : t('stampCard.addStamps.submitButton', {
                    fallback: 'Add 1 Stamp',
                    data: { count: 1 },
                  })}
            </Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

export default AddStampsModal;
